import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { ComplianceTabs } from "./ComplianceTabs";

interface CompliancePageShellProps {
  readonly screenTestId: string;
  readonly titleKey: string;
  readonly subtitleKey: string;
  readonly headerActions?: ReactNode;
  readonly children: ReactNode;
}

/**
 * Shared shell for every `/compliance/*` page: eyebrow + title +
 * subtitle, read-only banner for non-admin roles, internal sub-nav.
 * Keeps the four pages visually consistent without duplicating the
 * scaffolding.
 */
export function CompliancePageShell({
  screenTestId,
  titleKey,
  subtitleKey,
  headerActions,
  children,
}: CompliancePageShellProps) {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  return (
    <div data-testid={screenTestId} className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        {/*
          max-w on the title block keeps the headerActions button anchored
          top-right even when a future subtitle ends up long enough to
          otherwise wrap the button below via flex-wrap. Without this the
          locales page hit exactly that bug at 944ba2e.
        */}
        <div className="max-w-3xl space-y-2">
          <p className="brand-eyebrow">{t("compliance.eyebrow")}</p>
          <h1 className="text-4xl">{t(titleKey)}</h1>
          <p className="font-sans text-base font-normal text-muted-foreground">{t(subtitleKey)}</p>
        </div>
        {headerActions}
      </header>

      {!canEdit && (
        <div
          data-testid="compliance-readonly-banner"
          className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
        >
          <Info className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <p className="font-sans text-sm font-normal text-muted-foreground">
            {t("compliance.readOnlyBanner")}
          </p>
        </div>
      )}

      <ComplianceTabs />

      {children}
    </div>
  );
}
